import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../supabaseClient';

/**
 * VoiceBrandForm — Role questionnaire for the Voice-Brand Alignment add-on.
 * 8 fields: job title, industry, website (optional), org size, role description,
 * key audiences, communication challenges (optional), brand values (optional).
 *
 * Submits to save-voice-brand-input serverless function, then redirects
 * back to the Client Portal.
 */
function VoiceBrandForm() {
  const { user, profile, loading: authLoading, signOut } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Form fields
  const [jobTitle, setJobTitle] = useState('');
  const [industry, setIndustry] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [orgSize, setOrgSize] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [keyAudiences, setKeyAudiences] = useState('');
  const [communicationChallenges, setCommunicationChallenges] = useState('');
  const [brandValues, setBrandValues] = useState('');

  // Redirect if not purchased
  useEffect(() => {
    if (!authLoading && profile && profile.voice_brand_purchased !== true) {
      window.location.href = '/results';
    }
  }, [authLoading, profile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('You are not logged in. Please sign in again.');
        setSubmitting(false);
        return;
      }

      const response = await fetch('/.netlify/functions/save-voice-brand-input', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          job_title: jobTitle.trim(),
          industry: industry.trim(),
          website_url: websiteUrl.trim(),
          org_size: orgSize,
          role_description: roleDescription.trim(),
          key_audiences: keyAudiences.trim(),
          communication_challenges: communicationChallenges.trim(),
          brand_values: brandValues.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to save your responses. Please try again.');
        setSubmitting(false);
        return;
      }

      setSubmitted(true);
      // Redirect to portal after brief confirmation
      setTimeout(() => {
        window.location.href = '/results';
      }, 2000);
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      setSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div style={styles.loadingContainer}>
        <p style={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={styles.page}>
        <header style={styles.header}>
          <div style={styles.headerInner}>
            <img src="/pass-logo.png" alt="PASS Profile 3.0" style={styles.logo} />
          </div>
        </header>
        <main style={styles.main}>
          <div style={styles.successCard}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>&#10003;</div>
            <h2 style={styles.successTitle}>Your Role Profile Has Been Submitted!</h2>
            <p style={styles.successText}>
              We're generating your Voice-Brand Alignment report now. You'll receive it
              via email and it will appear in your portal.
            </p>
            <p style={styles.successSub}>Redirecting to your portal...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <img src="/pass-logo.png" alt="PASS Profile 3.0" style={styles.logo} />
          <button onClick={signOut} style={styles.signOutBtn}>Sign Out</button>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.container}>
          {/* Intro */}
          <div style={styles.introBanner}>
            <h1 style={styles.introTitle}>Voice-Brand Alignment</h1>
            <p style={styles.introSubtitle}>
              Tell us about your role. Your report will apply the approaches your PASS
              answers favored to your specific job, the people you communicate with, and
              the conversations that matter most in your work.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={styles.form}>
            {error && <div style={styles.errorBox}>{error}</div>}

            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                Job Title <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                required
                disabled={submitting}
                placeholder='e.g., "Sales Director", "Executive Coach", "Team Lead"'
                style={styles.input}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                Industry <span style={styles.required}>*</span>
              </label>
              <p style={styles.fieldHint}>
                Your field or sector. It's fine to be broad or specific.
              </p>
              <input
                type="text"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                required
                disabled={submitting}
                placeholder='e.g., "Healthcare Technology", "Financial Services", "Coaching", "Music & Performing Arts"'
                style={styles.input}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Website (Optional)</label>
              <p style={styles.fieldHint}>
                Your company or personal website. It gives us more context about your work.
              </p>
              <input
                type="text"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                disabled={submitting}
                placeholder="e.g., www.yourcompany.com"
                style={styles.input}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Organization Size</label>
              <select
                value={orgSize}
                onChange={(e) => setOrgSize(e.target.value)}
                disabled={submitting}
                style={styles.select}
              >
                <option value="">Select...</option>
                <option value="Solo/Freelance">Solo / Freelance</option>
                <option value="Small (2-50)">Small (2-50 people)</option>
                <option value="Medium (51-500)">Medium (51-500 people)</option>
                <option value="Large (500+)">Large (500+ people)</option>
              </select>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                Brief Role Description <span style={styles.required}>*</span>
              </label>
              <p style={styles.fieldHint}>Describe what you do day-to-day in 2-3 sentences.</p>
              <textarea
                value={roleDescription}
                onChange={(e) => setRoleDescription(e.target.value)}
                required
                disabled={submitting}
                placeholder="What does a typical week look like? What are your main responsibilities?"
                style={styles.textarea}
                rows={3}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                Key Audiences <span style={styles.required}>*</span>
              </label>
              <p style={styles.fieldHint}>
                Who do you communicate with most often?
              </p>
              <textarea
                value={keyAudiences}
                onChange={(e) => setKeyAudiences(e.target.value)}
                required
                disabled={submitting}
                placeholder="e.g., C-suite execs, direct reports, clients, cross-functional teams, students, patients"
                style={styles.textarea}
                rows={2}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Communication Challenges</label>
              <p style={styles.fieldHint}>
                What communication situations feel hardest or most important in your role? (Optional)
              </p>
              <textarea
                value={communicationChallenges}
                onChange={(e) => setCommunicationChallenges(e.target.value)}
                disabled={submitting}
                placeholder="e.g., Giving difficult feedback, presenting to leadership, motivating clients, navigating tough conversations, speaking up in group settings"
                style={styles.textarea}
                rows={2}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Brand Values</label>
              <p style={styles.fieldHint}>
                What qualities do you want people to associate with you? Think about the impression you want to leave. (Optional)
              </p>
              <textarea
                value={brandValues}
                onChange={(e) => setBrandValues(e.target.value)}
                disabled={submitting}
                placeholder="e.g., trustworthy, innovative, approachable, inspiring, authentic, confident"
                style={styles.textarea}
                rows={2}
              />
            </div>

            <div style={styles.formActions}>
              <a href="/results" style={styles.cancelLink}>Back to Portal</a>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  ...styles.submitBtn,
                  ...(submitting ? styles.submitBtnDisabled : {}),
                }}
              >
                {submitting ? 'Submitting...' : 'Generate My Voice-Brand Report'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

/* ===== STYLES ===== */
const styles = {
  page: {
    minHeight: '100vh',
    background: '#F8F9FA',
    fontFamily: 'Inter, system-ui, sans-serif',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#F8F9FA',
  },
  loadingText: {
    color: '#6B7A8F',
    fontSize: 16,
  },
  header: {
    backgroundColor: '#FFFFFF',
    borderBottom: '3px solid #2A9D8F',
    padding: '16px 24px',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  headerInner: {
    maxWidth: 1100,
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    height: '40px',
  },
  signOutBtn: {
    background: 'transparent',
    border: '1px solid #0B3D2E',
    color: '#0B3D2E',
    padding: '8px 16px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
  },
  main: {
    flex: 1,
    padding: '32px 0',
  },
  container: {
    maxWidth: 700,
    margin: '0 auto',
    padding: '0 24px',
  },

  // Intro banner
  introBanner: {
    background: 'linear-gradient(135deg, #0B3D2E 0%, #2A9D8F 100%)',
    borderRadius: 12,
    padding: '32px 40px',
    marginBottom: 32,
    textAlign: 'center',
  },
  introTitle: {
    color: 'white',
    fontSize: 28,
    fontWeight: 700,
    margin: '0 0 12px 0',
    fontFamily: 'Outfit, Inter, sans-serif',
  },
  introSubtitle: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 15,
    lineHeight: 1.6,
    margin: 0,
  },

  // Form
  form: {
    background: 'white',
    borderRadius: 12,
    padding: '32px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #E8ECF0',
  },
  errorBox: {
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 8,
    padding: '12px 16px',
    color: '#DC2626',
    fontSize: 14,
    marginBottom: 24,
  },
  fieldGroup: {
    marginBottom: 24,
  },
  label: {
    display: 'block',
    fontSize: 15,
    fontWeight: 600,
    color: '#0B3D2E',
    marginBottom: 6,
  },
  required: {
    color: '#DC2626',
  },
  fieldHint: {
    fontSize: 13,
    color: '#6B7A8F',
    margin: '0 0 8px 0',
    lineHeight: 1.4,
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    fontSize: 15,
    border: '1px solid #D1D5DB',
    borderRadius: 8,
    fontFamily: 'Inter, system-ui, sans-serif',
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '12px 14px',
    fontSize: 15,
    border: '1px solid #D1D5DB',
    borderRadius: 8,
    fontFamily: 'Inter, system-ui, sans-serif',
    outline: 'none',
    background: 'white',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    fontSize: 15,
    border: '1px solid #D1D5DB',
    borderRadius: 8,
    fontFamily: 'Inter, system-ui, sans-serif',
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  formActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 32,
    paddingTop: 24,
    borderTop: '1px solid #E8ECF0',
  },
  cancelLink: {
    color: '#6B7A8F',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 500,
  },
  submitBtn: {
    background: '#2A9D8F',
    color: 'white',
    padding: '14px 28px',
    borderRadius: 8,
    border: 'none',
    fontWeight: 600,
    fontSize: 15,
    cursor: 'pointer',
    transition: 'background 0.2s',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  submitBtnDisabled: {
    background: '#9CA3AF',
    cursor: 'not-allowed',
  },

  // Success state
  successCard: {
    background: 'white',
    borderRadius: 12,
    padding: '48px 40px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #E8ECF0',
    textAlign: 'center',
    maxWidth: 600,
    margin: '64px auto',
  },
  successTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#0B3D2E',
    margin: '0 0 12px 0',
  },
  successText: {
    fontSize: 15,
    color: '#6B7A8F',
    lineHeight: 1.6,
    margin: '0 0 16px 0',
  },
  successSub: {
    fontSize: 13,
    color: '#6B7A8F',
    fontStyle: 'italic',
  },
};

export default VoiceBrandForm;
